import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  complianceApi,
  type TdsReceivableEntry,
  type TdsReceivableSummary,
  type TdsReminderEmail,
} from '@/api/compliance.api';
import { formatINR } from '@/utils/format';
import { useToast } from '@/contexts/ToastContext';

const STATUS_STYLE: Record<string, { label: string; cls: string }> = {
  EXPECTED: { label: 'EXPECTED', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  CERTIFICATE_PENDING: { label: 'PENDING', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  RECEIVED: { label: 'RECEIVED', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  RECONCILED: { label: 'RECONCILED', cls: 'bg-teal-50 text-teal-700 border-teal-200' },
  CLAIMED: { label: 'CLAIMED', cls: 'bg-green-50 text-green-700 border-green-200' },
  OVERDUE: { label: 'OVERDUE', cls: 'bg-red-50 text-red-600 border-red-200' },
};

const QUARTER_LABEL: Record<string, string> = {
  Q1: 'Q1 (Apr-Jun)',
  Q2: 'Q2 (Jul-Sep)',
  Q3: 'Q3 (Oct-Dec)',
  Q4: 'Q4 (Jan-Mar)',
};

function currentFinancialYear(): string {
  const d = new Date();
  const month = d.getMonth();
  const year = d.getFullYear();
  const fyStart = month >= 3 ? year : year - 1;
  return `${fyStart}-${String(fyStart + 1).slice(-2)}`;
}

function fyOptions(): string[] {
  const cur = parseInt(currentFinancialYear().split('-')[0], 10);
  return [cur - 1, cur, cur + 1].map((y) => `${y}-${String(y + 1).slice(-2)}`);
}

function formatDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

interface Props {
  /** Opens the Quarterly Return page's GSTR2X Reconciliation section (Stage 8) */
  onReconcile?: () => void;
}

export default function TdsReceivableTab({ onReconcile }: Props) {
  const toast = useToast();
  const navigate = useNavigate();

  const [entries, setEntries] = useState<TdsReceivableEntry[]>([]);
  const [summary, setSummary] = useState<TdsReceivableSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [financialYear, setFinancialYear] = useState(currentFinancialYear());
  const [quarter, setQuarter] = useState('ALL');
  const [status, setStatus] = useState('ALL');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [certModalEntry, setCertModalEntry] = useState<TdsReceivableEntry | null>(null);
  const [reminderModal, setReminderModal] = useState<TdsReminderEmail | null>(null);
  const [bulkReminderModal, setBulkReminderModal] = useState<{
    emails: (TdsReminderEmail & { entryId: string })[];
    totalPending: number;
    count: number;
  } | null>(null);
  const [sendingReminderId, setSendingReminderId] = useState<string | null>(null);
  const [sendingBulk, setSendingBulk] = useState(false);
  const [claimingId, setClaimingId] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [entriesRes, summaryRes] = await Promise.all([
        complianceApi.getTdsReceivable({
          financialYear,
          quarter: quarter !== 'ALL' ? quarter : undefined,
          status: status !== 'ALL' ? status : undefined,
          search: search || undefined,
        }),
        complianceApi.getTdsReceivableSummary(financialYear),
      ]);
      setEntries(entriesRes?.success ? entriesRes.data || [] : []);
      setSummary(summaryRes?.success ? summaryRes.data || null : null);
    } catch {
      toast.error('Failed to load TDS receivable data');
    } finally {
      setLoading(false);
    }
  }, [financialYear, quarter, status, search, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSendReminder = async (id: string) => {
    setSendingReminderId(id);
    try {
      const res = await complianceApi.getTdsReminderEmail(id);
      if (res?.success && res.data) {
        setReminderModal(res.data);
      } else {
        toast.error(res?.message || 'Failed to build reminder email');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to build reminder email');
    } finally {
      setSendingReminderId(null);
    }
  };

  const handleBulkReminder = async () => {
    if (quarter === 'ALL') {
      toast.error('Select a specific quarter to send a bulk reminder');
      return;
    }
    setSendingBulk(true);
    try {
      const res = await complianceApi.getTdsBulkReminderEmails(financialYear, quarter);
      if (res?.success && res.data) {
        if (res.data.count === 0) {
          toast.info('No pending certificates for this quarter');
        } else {
          setBulkReminderModal(res.data);
        }
      } else {
        toast.error(res?.message || 'Failed to build bulk reminders');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to build bulk reminders');
    } finally {
      setSendingBulk(false);
    }
  };

  const handleMarkClaimed = async (id: string) => {
    if (!window.confirm('Mark this TDS entry as claimed in your Income Tax Return?')) return;
    setClaimingId(id);
    try {
      const res = await complianceApi.markTdsClaimed(id);
      if (res?.success) {
        toast.success('Marked as claimed');
        load();
      } else {
        toast.error(res?.message || 'Failed to update status');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update status');
    } finally {
      setClaimingId(null);
    }
  };

  const pendingCount = entries.filter((e) => e.displayStatus === 'CERTIFICATE_PENDING' || e.displayStatus === 'OVERDUE').length;

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-indigo-50">
            <i className="ri-wallet-3-line text-[#4f46e5]" />
          </div>
          <div>
            <p className="text-xs text-slate-400">FY Total TDS</p>
            <p className="text-lg font-bold text-[#4f46e5]">{formatINR(summary?.fyTotalTds || 0)}</p>
          </div>
        </div>
        <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-amber-50">
            <i className="ri-time-line text-amber-600" />
          </div>
          <div>
            <p className="text-xs text-slate-400">Cert Pending</p>
            <p className="text-lg font-bold text-amber-600">
              {summary?.certPendingCount ?? 0} <span className="text-xs font-medium text-slate-400">({formatINR(summary?.certPendingAmount || 0)})</span>
            </p>
          </div>
        </div>
        <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-50">
            <i className="ri-checkbox-circle-line text-blue-600" />
          </div>
          <div>
            <p className="text-xs text-slate-400">Received</p>
            <p className="text-lg font-bold text-blue-600">{formatINR(summary?.receivedAmount || 0)}</p>
          </div>
        </div>
        <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-slate-100">
            <i className="ri-calendar-event-line text-slate-500" />
          </div>
          <div>
            <p className="text-xs text-slate-400">Next Due</p>
            <p className="text-sm font-bold text-[#1e293b]">
              {summary?.nextDue ? `${QUARTER_LABEL[summary.nextDue.quarter] || summary.nextDue.quarter} by ${formatDate(summary.nextDue.date)}` : '—'}
            </p>
          </div>
        </div>
      </div>

      {/* Due-date banner */}
      {summary?.nextDue && (
        <div
          className={`flex items-center gap-2.5 rounded-xl px-4 py-3 border ${
            new Date() > new Date(summary.nextDue.date) ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
          }`}
        >
          <i className={`ri-alarm-warning-line ${new Date() > new Date(summary.nextDue.date) ? 'text-red-600' : 'text-amber-600'}`} />
          <p className={`text-sm font-medium ${new Date() > new Date(summary.nextDue.date) ? 'text-red-700' : 'text-amber-700'}`}>
            {QUARTER_LABEL[summary.nextDue.quarter] || summary.nextDue.quarter} {summary.nextDue.financialYear} Form 16A must be received by{' '}
            {formatDate(summary.nextDue.date)}. {pendingCount} certificate{pendingCount === 1 ? '' : 's'} pending.
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={financialYear}
          onChange={(e) => setFinancialYear(e.target.value)}
          className="h-9 px-3 rounded-lg border border-[#e2e8f0] text-sm bg-white text-slate-600 focus:outline-none focus:border-[#4f46e5]"
        >
          {fyOptions().map((fy) => (
            <option key={fy} value={fy}>{fy}</option>
          ))}
        </select>
        <select
          value={quarter}
          onChange={(e) => setQuarter(e.target.value)}
          className="h-9 px-3 rounded-lg border border-[#e2e8f0] text-sm bg-white text-slate-600 focus:outline-none focus:border-[#4f46e5]"
        >
          <option value="ALL">All Quarters</option>
          {Object.entries(QUARTER_LABEL).map(([k, l]) => (
            <option key={k} value={k}>{l}</option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-9 px-3 rounded-lg border border-[#e2e8f0] text-sm bg-white text-slate-600 focus:outline-none focus:border-[#4f46e5]"
        >
          <option value="ALL">All Status</option>
          <option value="EXPECTED">Expected</option>
          <option value="CERTIFICATE_PENDING">Certificate Pending</option>
          <option value="OVERDUE">Overdue</option>
          <option value="RECEIVED">Received</option>
          <option value="RECONCILED">Reconciled</option>
          <option value="CLAIMED">Claimed</option>
        </select>
        <div className="relative flex-1 max-w-xs">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search customer, PAN, invoice..."
            className="w-full h-9 pl-9 pr-3 rounded-lg border border-[#e2e8f0] text-sm bg-white focus:outline-none focus:border-[#4f46e5]"
          />
        </div>
        <button
          onClick={() => { setQuarter('ALL'); setStatus('ALL'); setSearchInput(''); }}
          className="text-xs text-[#4f46e5] font-medium cursor-pointer hover:underline whitespace-nowrap"
        >
          Reset
        </button>
        {quarter !== 'ALL' && (
          <button
            onClick={handleBulkReminder}
            disabled={sendingBulk}
            className="ml-auto flex items-center gap-1.5 h-9 px-3 rounded-lg bg-amber-500 text-white text-xs font-semibold hover:bg-amber-600 disabled:opacity-50 cursor-pointer whitespace-nowrap transition-colors"
          >
            {sendingBulk ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-mail-send-line" />}
            Send Bulk Reminder
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500 border-b border-[#e2e8f0] bg-[#f8fafc]">
              <th className="py-3 px-4 font-medium">Customer / PAN</th>
              <th className="py-3 px-4 font-medium">Invoice</th>
              <th className="py-3 px-4 font-medium text-right">Amount</th>
              <th className="py-3 px-4 font-medium">Section</th>
              <th className="py-3 px-4 font-medium">Quarter</th>
              <th className="py-3 px-4 font-medium">Certificate</th>
              <th className="py-3 px-4 font-medium">Status</th>
              <th className="py-3 px-4 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="py-12 text-center text-slate-400"><i className="ri-loader-4-line animate-spin text-2xl block mb-2" />Loading…</td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan={8} className="py-12 text-center text-slate-400"><i className="ri-inbox-line text-3xl block mb-2" /><p className="text-sm">No TDS receivable entries found</p></td></tr>
            ) : (
              entries.map((e) => {
                const badge = STATUS_STYLE[e.displayStatus] || STATUS_STYLE.EXPECTED;
                return (
                  <tr key={e.id} className="border-b border-slate-50 hover:bg-indigo-50/20 transition-colors">
                    <td className="py-3 px-4">
                      <p className="font-medium text-[#1e293b]">{e.partyName}</p>
                      <p className="text-xs text-slate-400 font-mono">{e.partyPAN || '—'}</p>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-[#1e293b]">{e.invoiceNumber}</p>
                      <p className="text-xs text-slate-400">{formatDate(e.paymentDate)}</p>
                    </td>
                    <td className="py-3 px-4 text-right font-semibold text-[#1e293b]">
                      {formatINR(e.tdsAmount)}
                      <p className="text-xs text-slate-400 font-normal">{e.tdsRate ?? '—'}%</p>
                    </td>
                    <td className="py-3 px-4 text-slate-600">{e.section || '—'}</td>
                    <td className="py-3 px-4">
                      <p className="text-slate-600">{e.quarter}</p>
                      <p className="text-xs text-slate-400">{e.financialYear}</p>
                    </td>
                    <td className="py-3 px-4">
                      {e.certificateNo ? (
                        <span className="text-xs font-mono text-slate-600">{e.certificateNo}</span>
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                      {e.hasAmountMismatch && (
                        <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200 font-semibold">
                          ⚠ ₹{e.amountMismatchValue}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-medium whitespace-nowrap ${badge.cls}`}>{badge.label}</span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        <button
                          onClick={() => navigate('/sales/invoices')}
                          title="View Invoice"
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-[#4f46e5] hover:bg-indigo-50 cursor-pointer transition-colors"
                        >
                          <i className="ri-eye-line text-sm" />
                        </button>
                        {(e.displayStatus === 'EXPECTED' || e.displayStatus === 'CERTIFICATE_PENDING' || e.displayStatus === 'OVERDUE') && (
                          <button
                            onClick={() => handleSendReminder(e.id)}
                            disabled={sendingReminderId === e.id}
                            title="Send Reminder"
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50 disabled:opacity-50 cursor-pointer transition-colors"
                          >
                            {sendingReminderId === e.id ? <i className="ri-loader-4-line animate-spin text-sm" /> : <i className="ri-mail-send-line text-sm" />}
                          </button>
                        )}
                        {(e.displayStatus === 'CERTIFICATE_PENDING' || e.displayStatus === 'OVERDUE') && !e.isVirtual && (
                          <button
                            onClick={() => setCertModalEntry(e)}
                            className="flex items-center gap-1 h-7 px-2.5 rounded-lg bg-[#4f46e5] text-white text-xs font-semibold hover:bg-indigo-700 cursor-pointer whitespace-nowrap transition-colors"
                          >
                            <i className="ri-file-add-line text-xs" /> Add Certificate
                          </button>
                        )}
                        {e.displayStatus === 'RECEIVED' && onReconcile && (
                          <button
                            onClick={onReconcile}
                            title="Reconcile with GSTN"
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 cursor-pointer transition-colors"
                          >
                            <i className="ri-refresh-line text-sm" />
                          </button>
                        )}
                        {(e.displayStatus === 'RECEIVED' || e.displayStatus === 'RECONCILED') && (
                          <button
                            onClick={() => handleMarkClaimed(e.id)}
                            disabled={claimingId === e.id}
                            className="flex items-center gap-1 h-7 px-2.5 rounded-lg border border-emerald-300 text-emerald-700 text-xs font-semibold hover:bg-emerald-50 disabled:opacity-50 cursor-pointer whitespace-nowrap transition-colors"
                          >
                            {claimingId === e.id ? <i className="ri-loader-4-line animate-spin text-xs" /> : <i className="ri-checkbox-circle-line text-xs" />} Mark Claimed
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {certModalEntry && (
        <AddCertificateModal
          entry={certModalEntry}
          onClose={() => setCertModalEntry(null)}
          onSaved={load}
        />
      )}

      {reminderModal && (
        <ReminderEmailModal email={reminderModal} onClose={() => setReminderModal(null)} />
      )}

      {bulkReminderModal && (
        <BulkReminderModal data={bulkReminderModal} onClose={() => setBulkReminderModal(null)} />
      )}
    </div>
  );
}

// ─── Add Certificate Modal ──────────────────────────────────────────────────

function AddCertificateModal({
  entry,
  onClose,
  onSaved,
}: {
  entry: TdsReceivableEntry;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [certificateNo, setCertificateNo] = useState('');
  const [certificateDate, setCertificateDate] = useState('');
  const [certTdsAmount, setCertTdsAmount] = useState<number | ''>(entry.tdsAmount);
  const [challanNo, setChallanNo] = useState('');
  const [bsrCode, setBsrCode] = useState('');
  const [remarks, setRemarks] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const certAmountValue = Number(certTdsAmount) || 0;
  const diff = Number(Math.abs(certAmountValue - entry.tdsAmount).toFixed(2));
  const hasMismatch = certAmountValue > 0 && diff > 0;
  const bigMismatch = diff > 10;

  const handleSave = async () => {
    if (!certificateNo.trim()) return toast.error('Certificate number is required');
    if (!certificateDate) return toast.error('Certificate date is required');
    if (new Date(certificateDate) > new Date()) return toast.error('Certificate date cannot be in future');
    if (!certAmountValue || certAmountValue <= 0) return toast.error('TDS amount on certificate is required');
    if (bigMismatch) {
      toast.error(`Certificate amount differs significantly from our record by ₹${diff}. Verify with customer before saving.`);
      return;
    }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('certificateNo', certificateNo.trim());
      fd.append('certificateDate', certificateDate);
      fd.append('certTdsAmount', String(certAmountValue));
      if (challanNo.trim()) fd.append('challanNo', challanNo.trim());
      if (bsrCode.trim()) fd.append('bsrCode', bsrCode.trim());
      if (remarks.trim()) fd.append('remarks', remarks.trim());
      if (file) fd.append('certificateFile', file);

      const res = await complianceApi.saveTdsCertificate(entry.id, fd);
      if (res?.success) {
        toast.success(res.message || 'Certificate recorded.');
        (res.warnings || []).forEach((w) => toast.warning(w));
        onSaved();
        onClose();
      } else {
        toast.error(res?.message || 'Failed to save certificate');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to save certificate');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
          <h3 className="text-base font-bold text-[#1e293b]">Add TDS Certificate — Form 16A</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 cursor-pointer">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-slate-50 border border-[#e2e8f0] rounded-lg p-3 grid grid-cols-2 gap-2.5 text-xs">
            <div><p className="text-slate-400">Customer</p><p className="font-semibold text-[#1e293b]">{entry.partyName}</p></div>
            <div><p className="text-slate-400">Invoice</p><p className="font-semibold text-[#1e293b]">{entry.invoiceNumber}</p></div>
            <div><p className="text-slate-400">TDS Amount</p><p className="font-semibold text-[#1e293b]">{formatINR(entry.tdsAmount)}</p></div>
            <div><p className="text-slate-400">Section</p><p className="font-semibold text-[#1e293b]">{entry.section}</p></div>
            <div><p className="text-slate-400">Quarter</p><p className="font-semibold text-[#1e293b]">{QUARTER_LABEL[entry.quarter] || entry.quarter}</p></div>
            <div><p className="text-slate-400">FY</p><p className="font-semibold text-[#1e293b]">{entry.financialYear}</p></div>
            <div><p className="text-slate-400">Customer PAN</p><p className="font-semibold text-[#1e293b] font-mono">{entry.partyPAN || '—'}</p></div>
            <div><p className="text-slate-400">Customer TAN</p><p className="font-semibold text-[#1e293b] font-mono">{entry.partyTan || '—'}</p></div>
          </div>

          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Certificate Details</p>

          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Certificate Number <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={certificateNo}
              onChange={(e) => setCertificateNo(e.target.value)}
              placeholder="16A-XXXX-Q2-2026-001"
              className="w-full h-9 px-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]"
            />
            <p className="text-[11px] text-slate-400 mt-1">From the Form 16A PDF given by the customer</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Certificate Date <span className="text-red-500">*</span></label>
              <input
                type="date"
                value={certificateDate}
                onChange={(e) => setCertificateDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className="w-full h-9 px-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">TDS Amount on Certificate <span className="text-red-500">*</span></label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold text-sm">₹</span>
                <input
                  type="number"
                  value={certTdsAmount === '' ? '' : certTdsAmount}
                  onChange={(e) => setCertTdsAmount(e.target.value === '' ? '' : Number(e.target.value))}
                  className="w-full h-9 pl-7 pr-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Challan No (if available)</label>
              <input
                type="text"
                value={challanNo}
                onChange={(e) => setChallanNo(e.target.value)}
                placeholder="2345678"
                className="w-full h-9 px-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">BSR Code (if available)</label>
              <input
                type="text"
                value={bsrCode}
                onChange={(e) => setBsrCode(e.target.value.replace(/\D/g, '').slice(0, 7))}
                placeholder="1234567"
                maxLength={7}
                className="w-full h-9 px-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] font-mono"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Upload Certificate PDF (optional)</label>
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-xs text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-[#4f46e5] hover:file:bg-indigo-100 file:cursor-pointer cursor-pointer"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Remarks</label>
            <input
              type="text"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="e.g. Received via email on 15 Nov 2026"
              className="w-full h-9 px-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]"
            />
          </div>

          <div className={`rounded-lg p-3 border ${bigMismatch ? 'bg-red-50 border-red-200' : hasMismatch ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
            <p className="text-xs font-bold uppercase tracking-wide mb-1.5 text-slate-500">Amount Verification</p>
            <div className="flex justify-between text-xs mb-0.5">
              <span className="text-slate-500">Our TDS Record</span>
              <span className="font-semibold text-[#1e293b]">{formatINR(entry.tdsAmount)}</span>
            </div>
            <div className="flex justify-between text-xs mb-0.5">
              <span className="text-slate-500">Certificate Amt</span>
              <span className="font-semibold text-[#1e293b]">{formatINR(certAmountValue)}</span>
            </div>
            <div className="flex justify-between text-xs pt-1 border-t border-black/5">
              <span className="text-slate-500">Difference</span>
              <span className={`font-bold ${bigMismatch ? 'text-red-600' : hasMismatch ? 'text-amber-600' : 'text-emerald-600'}`}>
                {formatINR(diff)} {bigMismatch ? '⚠️ MISMATCH' : hasMismatch ? '⚠️ Minor diff' : '✅ MATCH'}
              </span>
            </div>
            {bigMismatch && (
              <p className="text-[11px] text-red-600 mt-1.5">Consult your CA — large differences need query before saving.</p>
            )}
            {hasMismatch && !bigMismatch && (
              <p className="text-[11px] text-amber-600 mt-1.5">Small rounding differences are acceptable.</p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[#e2e8f0]">
          <button onClick={onClose} className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || bigMismatch}
            className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 cursor-pointer whitespace-nowrap"
          >
            {saving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-checkbox-circle-line" />} Save Certificate
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Reminder Email Modals (mailto / copy — no SMTP configured in this project) ──

function ReminderEmailModal({ email, onClose }: { email: TdsReminderEmail; onClose: () => void }) {
  const toast = useToast();
  const copy = () => {
    navigator.clipboard.writeText(`To: ${email.to}\nSubject: ${email.subject}\n\n${email.body}`);
    toast.success('Email copied to clipboard');
  };
  const mailtoHref = `mailto:${encodeURIComponent(email.to)}?subject=${encodeURIComponent(email.subject)}&body=${encodeURIComponent(email.body)}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
          <h3 className="text-base font-bold text-[#1e293b]">Send Reminder — TDS Certificate</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
        </div>
        <div className="p-6 space-y-3">
          {!email.to && (
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700">
              <i className="ri-alert-line mt-0.5 shrink-0" /> No email on file for this customer — add one in Party Master.
            </div>
          )}
          <div className="text-xs">
            <p className="text-slate-400 mb-0.5">To</p>
            <p className="font-medium text-[#1e293b]">{email.to || '—'}</p>
          </div>
          <div className="text-xs">
            <p className="text-slate-400 mb-0.5">Subject</p>
            <p className="font-medium text-[#1e293b]">{email.subject}</p>
          </div>
          <div className="text-xs">
            <p className="text-slate-400 mb-1">Body</p>
            <pre className="whitespace-pre-wrap font-sans text-xs bg-slate-50 border border-[#e2e8f0] rounded-lg p-3 text-[#1e293b]">{email.body}</pre>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[#e2e8f0]">
          <button onClick={copy} className="flex items-center gap-1.5 h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap">
            <i className="ri-file-copy-line" /> Copy Email
          </button>
          <a
            href={mailtoHref}
            className={`flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer whitespace-nowrap ${!email.to ? 'pointer-events-none opacity-50' : ''}`}
          >
            <i className="ri-mail-open-line" /> Open in Mail Client
          </a>
        </div>
      </div>
    </div>
  );
}

function BulkReminderModal({
  data,
  onClose,
}: {
  data: { emails: (TdsReminderEmail & { entryId: string })[]; totalPending: number; count: number };
  onClose: () => void;
}) {
  const toast = useToast();
  const copyAll = () => {
    const text = data.emails.map((e) => `To: ${e.to}\nSubject: ${e.subject}\n\n${e.body}`).join('\n\n───────────\n\n');
    navigator.clipboard.writeText(text);
    toast.success('All reminder emails copied to clipboard');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
          <h3 className="text-base font-bold text-[#1e293b]">Bulk Reminder Sent</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
        </div>
        <div className="p-6 space-y-3">
          <p className="text-sm text-slate-600">
            {data.count} pending certificate{data.count === 1 ? '' : 's'} · Total pending {formatINR(data.totalPending)}
          </p>
          <div className="border border-[#e2e8f0] rounded-lg divide-y divide-[#f1f5f9] max-h-64 overflow-y-auto">
            {data.emails.map((e) => (
              <div key={e.entryId} className="px-3 py-2 text-xs">
                <p className="font-semibold text-[#1e293b]">{e.to || 'No email on file'}</p>
                <p className="text-slate-400">{e.subject}</p>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-400">No SMTP integration is configured — copy these templates and send via your mail client.</p>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[#e2e8f0]">
          <button onClick={copyAll} className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer whitespace-nowrap">
            <i className="ri-file-copy-line" /> Copy All
          </button>
        </div>
      </div>
    </div>
  );
}
